// LocationLayerPlugin.js
const BasePlugin = require('./basePlugin');
const { processGeoJSON, isValidLocation, getFeatureUid, isLocationClass, isRoomClass } = require('../mapUtils');
const QuickviewPopup = require('./quickviewPopup');
const TextMarker = require('./textMarker'); // We'll adjust this later
const edit = require('../../layout/components/edit');

class LocationLayerPlugin extends BasePlugin {
  constructor(ctrl) {
    super(ctrl);

    /**
     * Handles the addition of new location/architectural features (sites, buildings, levels, rooms) to the map.
     * @param {Array} features - Array of feature objects to add.
     */
    const handleFeatureAdditions = async function handleFeatureAdditions(features) {
      for (const feature of features) {
        let popup, textMarker;
        let locationRecord = feature.in ? feature.in : feature;

        // **1. Validate Feature**
        if (!isLocationClass(locationRecord['@class'])) return;
        if (!isValidLocation(locationRecord)) return;

        // **2. Retrieve Context Information**
        let contextRecord = ctrl.contexts.find(c => c.name === feature.context);
        const includePolygons = ctrl.locationConfig()?.polygons?.includes(contextRecord.name);
        if (!includePolygons) return;
        const defaultLocationStyle = ctrl.locationConfig()?.[contextRecord.name + 'Style'];
        const locationId = locationRecord['@rid'].toString().slice(1);
        locationRecord.context = contextRecord.name;

        // **3. Process GeoJSON to Create Polygon Layer**
        const layer = processGeoJSON(locationRecord, locationId, defaultLocationStyle);
        if (!layer) return;

        // Ensure all vectors render in the locations pane regardless of add order
        if (layer.getLayers) {
          layer.getLayers().forEach(featureLayer => {
            if (featureLayer && featureLayer.options) {
              featureLayer.options.pane = 'pane-locations';
              if (featureLayer.redraw) featureLayer.redraw();
            }
          });
        } else if (layer.options) {
          layer.options.pane = 'pane-locations';
        }

        // **4. Customize Polygon Style for Specific Contexts**
        if (contextRecord.name === 'this' && !['Site'].includes(locationRecord['@class'])) {
          layer.getLayers().forEach(featureLayer => {
            if (featureLayer?.feature?.properties?.name !== 'Bounding_Box') {
              featureLayer.setStyle({
                stroke: true,        // Enable stroke
                color: '#2196F3',    // Set stroke color (e.g., Blue)
                weight: 1,           // Set stroke width
                dashArray: 'none'    // No dashed lines
              });
            }
          });
        }

        // **5. Configure Polygon Layer Properties**
        layer.context = contextRecord.name;
        layer.UID = feature.UID;
        layer['@class'] = locationRecord['@class'];
        layer.options.interactive = true; // **Enable Interactivity on Polygon**

        // **6. Create and Add Text Marker (Non-Interactive)**
        try {
          if (['child', 'sibling'].includes(contextRecord.name) && isRoomClass(locationRecord['@class'])) {
            textMarker = await TextMarker(layer, locationRecord, false);

            if (textMarker && textMarker.options) {
              textMarker.options.pane = 'pane-labels';
            }

            // **Set Marker as Non-Interactive**
            textMarker.options.interactive = false;
            textMarker.options.opacity = 0.8;

            textMarker.UID = feature.UID;
            textMarker.context = contextRecord.name;
            ctrl.allLayers.push(textMarker);
            textMarker['@class'] = locationRecord['@class'];
            ctrl.locationParentLayer.addLayer(textMarker);
          }
        } catch (e) {
          console.error('Error adding text label', e);
        }

        // **7. Bind Popup with Toggle Logic**
        if (layer && layer.bindPopup && (layer.UID !== getFeatureUid(ctrl.location()))) {
          try {
            popup = QuickviewPopup(layer, locationRecord, ctrl.map);

            // Define a function that toggles the popup based on current view and edit mode
            const togglePopup = () => {
              if (!edit.vm.editing() && ctrl.mapSelectedView() === 'Overview' && layer.UID !== getFeatureUid(ctrl.location())) {
                // Only bind if it's not already bound
                if (!layer.isPopupBound) {
                  layer.bindPopup(popup, {
                    autoClose: false,
                    closeOnClick: false,
                    interactive: true
                  });
                  layer.isPopupBound = true;
                }
              } else {
                // If no longer 'Overview', unbind if it was bound
                if (layer.isPopupBound) {
                  layer.unbindPopup();
                  layer.isPopupBound = false;
                }
              }
            };

            // Initial call to set correct state
            togglePopup();

            // Subscribe to Mithril onupdate so we can re-check the condition
            // whenever view changes or other redraw cycles occur.
            const off = ctrl.eventBus.on('mithril:onupdate', togglePopup);

            // Also react immediately to edit mode toggles
            const onEditToggle = () => togglePopup();
            edit.on('toggle', onEditToggle);

            // Store the cleanup function on our layer so we can remove it later.
            layer.popupCleanup = () => {
              off();
              edit.off('toggle', onEditToggle);
            };

          } catch (e) {
            console.error('Error adding popup to polygon layer', e);
          }
        }

        // **8. Add Polygon Layer to Control Structures and Map**
        ctrl.allLayers.push(layer);
        ctrl.locationParentLayer.addLayer(layer);
      }
    };


    /**
     * Handles the removal of features from the map.
     * @param {Array} features - Array of feature UIDs to remove.
     */
    const handleFeatureRemovals = async function handleFeatureRemovals(features) {
      if (!ctrl.locationParentLayer) return;
      features.forEach(featureUid => {
        // **Find and Remove Layers with Matching UID**
        let featureLayers = ctrl.locationParentLayer.getLayers().filter(layer => layer.UID === featureUid);
        featureLayers.forEach(featureLayer => {
          try {
            // If we stored a 'popupCleanup' function, call it so we don't keep
            // subscribing to onupdate events once the layer is gone.
            if (featureLayer.popupCleanup) {
              featureLayer.popupCleanup();
              delete featureLayer.popupCleanup;
            }

            // Also unbind any bound popups explicitly if needed
            if (featureLayer.isPopupBound) {
              featureLayer.unbindPopup();
            }

            ctrl.locationParentLayer.removeLayer(featureLayer);
            let featureIndex = ctrl.allLayers.indexOf(featureLayer);
            if (featureIndex > -1) ctrl.allLayers.splice(featureIndex, 1);
          } catch (e) {
            // console.warn('Error removing layer', e);
          }
        });
      });
    };

    // **Initialize Parent Layer on Map Creation**
    ctrl.eventBus.on('mithril:oncreate', () => {
      ctrl.locationParentLayer = new L.featureGroup([], { pane: 'pane-locations' });
      ctrl.map.addLayer(ctrl.locationParentLayer);
    });

    // **Bind Event Handlers**
    ctrl.eventBus.on('featuresToAdd', handleFeatureAdditions);
    ctrl.eventBus.on('featuresToRemove', handleFeatureRemovals);
  }
}

module.exports = LocationLayerPlugin;
